import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import OrderCard from './components/OrderCard';
import { fetchMyOrders, fetchOrderByNo } from '@/api/order';
import type { MhOrderRow } from '@/types/mh';
import { getToken } from '@/lib/auth-storage';
import { ORDER_TABS, orderMatchesTab, type OrderTabKey } from '@/utils/order-display';

export default function OrdersPage() {
  const [activeTab, setActiveTab] = useState<OrderTabKey>('all');
  const [queryNo, setQueryNo] = useState('');
  const [showQuery, setShowQuery] = useState(false);
  const [orders, setOrders] = useState<MhOrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [queryResult, setQueryResult] = useState<MhOrderRow | null>(null);
  const [queryError, setQueryError] = useState('');
  const [queryLoading, setQueryLoading] = useState(false);

  const authed = !!getToken();

  const loadList = () => {
    if (!getToken()) {
      setOrders([]);
      setListError('');
      setLoading(false);
      return;
    }
    setLoading(true);
    setListError('');
    fetchMyOrders()
      .then((res) => {
        if (res.code === 0 && Array.isArray(res.data)) {
          setOrders(res.data);
        } else {
          setOrders([]);
          setListError(res.msg || '加载失败');
        }
      })
      .catch(() => {
        setOrders([]);
        setListError('网络异常');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadList();
    const onAuth = () => loadList();
    window.addEventListener('mh-auth-changed', onAuth);
    return () => window.removeEventListener('mh-auth-changed', onAuth);
  }, []);

  const filtered = useMemo(() => {
    if (activeTab === 'all') {
      return orders;
    }
    return orders.filter((o) => orderMatchesTab(o.status, activeTab));
  }, [orders, activeTab]);

  const tabCount = (key: OrderTabKey) => {
    if (key === 'all') {
      return orders.length;
    }
    return orders.filter((o) => orderMatchesTab(o.status, key)).length;
  };

  const handleQueryOne = async () => {
    const no = queryNo.trim();
    if (!no) {
      setQueryError('请输入订单号');
      return;
    }
    if (!getToken()) {
      setQueryError('请先登录');
      return;
    }
    setQueryLoading(true);
    setQueryError('');
    setQueryResult(null);
    try {
      const res = await fetchOrderByNo(no);
      if (res.code === 0 && res.data) {
        setQueryResult(res.data);
      } else {
        setQueryError(res.msg || '未找到订单');
      }
    } catch {
      setQueryError('网络异常');
    } finally {
      setQueryLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">我的订单</h1>
            <button
              onClick={() => setShowQuery(!showQuery)}
              className="flex items-center space-x-1.5 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-search-2-line" />
              </div>
              <span>查询订单</span>
            </button>
          </div>

          {!authed && (
            <div className="mb-5 rounded-2xl border border-gray-200 bg-white px-5 py-4 text-sm text-gray-600">
              请先
              <Link to="/login?redirect=%2Forders" className="text-gray-900 font-medium underline mx-1">
                登录
              </Link>
              后查看订单
            </div>
          )}

          {showQuery && authed && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-5">
              <p className="text-sm text-gray-500 mb-3">输入订单号查询（当前账号）</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={queryNo}
                  onChange={(e) => setQueryNo(e.target.value)}
                  placeholder="例如 MH202604131200001234"
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300 bg-gray-50 focus:bg-white transition-colors"
                />
                <button
                  type="button"
                  onClick={handleQueryOne}
                  disabled={queryLoading}
                  className="px-5 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {queryLoading ? '查询中…' : '查询'}
                </button>
              </div>
              {queryError && <p className="text-sm text-red-500 mt-2">{queryError}</p>}
              {queryResult && (
                <div className="mt-4">
                  <OrderCard order={queryResult} />
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-2xl border border-gray-100 mb-5 overflow-hidden">
            <nav className="flex overflow-x-auto">
              {ORDER_TABS.map((tab) => {
                const count = tabCount(tab.key);
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex-1 min-w-[72px] py-3.5 px-3 text-sm font-medium text-center whitespace-nowrap transition-colors cursor-pointer border-b-2 ${
                      isActive
                        ? 'text-gray-900 border-gray-900 bg-gray-50/50'
                        : 'text-gray-400 border-transparent hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {tab.label}
                    {count > 0 && (
                      <span
                        className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                          isActive ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {listError && authed && (
            <p className="text-sm text-red-500 mb-3">{listError}</p>
          )}

          {authed && loading ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center text-gray-500 text-sm">
              加载中…
            </div>
          ) : authed && filtered.length > 0 ? (
            <div className="space-y-4">
              {filtered.map((order) => (
                <OrderCard key={order.orderNo} order={order} />
              ))}
            </div>
          ) : authed ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-file-list-3-line text-2xl text-gray-400" />
                </div>
              </div>
              <h3 className="text-base font-semibold text-gray-700 mb-2">暂无订单</h3>
              <p className="text-gray-400 text-sm">在「查询服务」中下单后将显示在此</p>
            </div>
          ) : null}

          <div className="h-8" />
        </div>
      </div>
      <Footer />
    </div>
  );
}
